/**
 * 器材分配模組 - 核心邏輯
 * 處理品項可用性檢查、日期衝突排除、分配/取消操作、日誌記錄
 */

import { supabase } from '../../services/supabaseClient';
import type {
  AvailableItem,
  ItemConflict,
  AllocationRequest,
  AllocationResult,
  AllocationLogEntry,
  AdminInfo,
} from './types';

/**
 * 查詢指定產品的可分配品項
 * 規則：
 *  1. 狀態必須為 available（排除 rented / maintenance / retired）
 *  2. 排除在該訂單日期區間內已被其他訂單佔用的品項
 *
 * @param productId 產品 ID
 * @param startDate 訂單開始日期
 * @param endDate   訂單結束日期
 * @param excludeOrderId 排除自身訂單（避免自己佔自己）
 */
export async function fetchAvailableItems(
  productId: string,
  startDate: string,
  endDate: string,
  excludeOrderId?: string,
): Promise<{ items: AvailableItem[]; conflicts: ItemConflict[] }> {
  // 1. 取得該產品所有 available 狀態的品項
  // ✅ 補充 is_urent_equipment 欄位，讓前端可正確識別 Urent 品項
  const { data: allItems, error: itemsErr } = await supabase
    .from('equipment_items')
    .select(`
      id,
      serial_number,
      status,
      location_id,
      product_id,
      is_urent_equipment,
      urent_customer_id,
      locations:location_id ( name_zh )
    `)
    .eq('product_id', productId)
    .eq('status', 'available');

  if (itemsErr) throw itemsErr;
  if (!allItems || allItems.length === 0) {
    return { items: [], conflicts: [] };
  }

  const itemIds = allItems.map((i: any) => i.id);

  // 2. 查詢這些品項在日期區間內是否已被其他訂單佔用
  //    佔用條件：order_items.equipment_item_id = item.id
  //    且該訂單的日期區間與目標區間有重疊
  //    且訂單狀態不是 cancelled
  const { data: conflictRows, error: conflictErr } = await supabase
    .from('order_items')
    .select(`
      id,
      equipment_item_id,
      order_id,
      orders!inner (
        id,
        order_number,
        start_date,
        end_date,
        status
      )
    `)
    .in('equipment_item_id', itemIds)
    .not('orders.status', 'eq', 'cancelled');

  if (conflictErr) throw conflictErr;

  // 3. 過濾出日期有重疊的衝突
  const conflicts: ItemConflict[] = [];
  const occupiedItemIds = new Set<string>();

  (conflictRows || []).forEach((row: any) => {
    const order = row.orders;
    if (!order) return;
    // 排除自身訂單
    if (excludeOrderId && order.id === excludeOrderId) return;

    // 日期重疊判斷：A.start <= B.end && A.end >= B.start
    const oStart = new Date(order.start_date);
    const oEnd = new Date(order.end_date);
    const tStart = new Date(startDate);
    const tEnd = new Date(endDate);

    if (oStart <= tEnd && oEnd >= tStart) {
      occupiedItemIds.add(row.equipment_item_id);
      conflicts.push({
        item_id: row.equipment_item_id,
        order_id: order.id,
        order_number: order.order_number,
        start_date: order.start_date,
        end_date: order.end_date,
      });
    }
  });

  // 4. 過濾掉被佔用的品項
  const availableItems: AvailableItem[] = allItems
    .filter((i: any) => !occupiedItemIds.has(i.id))
    .map((i: any) => ({
      id: i.id,
      serial_number: i.serial_number,
      status: i.status,
      location_id: i.location_id,
      location_name_zh: i.locations?.name_zh || '未設定',
      product_id: i.product_id,
      // ✅ 補充：傳遞 Urent 相關欄位
      is_urent_equipment: i.is_urent_equipment || false,
      urent_customer_id: i.urent_customer_id || null,
    }));

  return { items: availableItems, conflicts };
}

/**
 * 執行庫存分配
 */
export async function allocateFromInventory(
  orderItemId: string,
  equipmentItemId: string,
  log: AllocationLogEntry,
): Promise<AllocationResult> {
  try {
    // 更新 order_items
    const { error: oiErr } = await supabase
      .from('order_items')
      .update({
        equipment_item_id: equipmentItemId,
        is_external_dispatch: false,
        external_dispatch_note: null,
      })
      .eq('id', orderItemId);

    if (oiErr) throw oiErr;

    // 更新 equipment_items 狀態為 rented
    const { error: eiErr } = await supabase
      .from('equipment_items')
      .update({ status: 'rented' })
      .eq('id', equipmentItemId);

    if (eiErr) throw eiErr;

    // 寫入日誌
    await writeAllocationLog(log);

    return { success: true, message: '器材分配成功' };
  } catch (err: any) {
    console.error('allocateFromInventory error:', err);
    return { success: false, message: err.message || '分配失敗' };
  }
}

/**
 * 執行同行調度分配
 */
export async function allocateExternal(
  orderItemId: string,
  dispatchNote: string,
  log: AllocationLogEntry,
): Promise<AllocationResult> {
  try {
    const { error: oiErr } = await supabase
      .from('order_items')
      .update({
        equipment_item_id: null,
        is_external_dispatch: true,
        external_dispatch_note: dispatchNote,
      })
      .eq('id', orderItemId);

    if (oiErr) throw oiErr;

    // 寫入日誌
    await writeAllocationLog(log);

    return { success: true, message: '同行調度分配成功' };
  } catch (err: any) {
    console.error('allocateExternal error:', err);
    return { success: false, message: err.message || '分配失敗' };
  }
}

/**
 * 取消分配
 */
export async function cancelAllocation(
  orderItemId: string,
  equipmentItemId: string | null,
  log: AllocationLogEntry,
): Promise<AllocationResult> {
  try {
    // 如果是庫存分配，將品項狀態改回 available
    if (equipmentItemId) {
      // 🆕 先查詢該器材是否為 Urent 品項
      const { data: itemData, error: queryErr } = await supabase
        .from('equipment_items')
        .select('id, serial_number, urent_customer_id, urent_status')
        .eq('id', equipmentItemId)
        .maybeSingle();

      if (queryErr) throw queryErr;

      // 🆕 準備更新資料
      const updateData: any = {
        status: 'available',
      };

      // 🆕 如果是 Urent 品項，同時將 urent_status 改回 idle
      if (itemData?.urent_customer_id) {
        updateData.urent_status = 'idle';
        console.log(`🟣 取消分配：Urent 品項 ${itemData.serial_number} 狀態將改回「閒置」`);
      }

      const { error: eiErr } = await supabase
        .from('equipment_items')
        .update(updateData)
        .eq('id', equipmentItemId);

      if (eiErr) throw eiErr;

      // 🆕 如果是 Urent 品項，記錄到日誌中
      if (itemData?.urent_customer_id) {
        console.log(`✅ Urent 品項 ${itemData.serial_number} 已取消分配，狀態改回「閒置」`);
      }
    }

    // 清除 order_items 的分配資訊
    const { error: oiErr } = await supabase
      .from('order_items')
      .update({
        equipment_item_id: null,
        is_external_dispatch: false,
        external_dispatch_note: null,
      })
      .eq('id', orderItemId);

    if (oiErr) throw oiErr;

    // 寫入日誌
    await writeAllocationLog(log);

    return { success: true, message: '已取消分配' };
  } catch (err: any) {
    console.error('cancelAllocation error:', err);
    return { success: false, message: err.message || '取消分配失敗' };
  }
}

/**
 * 寫入操作日誌到 order_logs
 */
export async function writeAllocationLog(
  entry: AllocationLogEntry,
): Promise<void> {
  const { error } = await supabase.from('order_logs').insert({
    order_id: entry.orderId,
    action_type: entry.actionType,
    description: entry.description,
    old_value: entry.oldValue || null,
    new_value: entry.newValue || null,
    admin_id: entry.adminId,
    admin_name: entry.adminName,
  });

  if (error) {
    console.error('writeAllocationLog error:', error);
  }
}

/**
 * 取得訂單的操作日誌
 */
export async function fetchOrderLogs(orderId: string) {
  const { data, error } = await supabase
    .from('order_logs')
    .select('*')
    .eq('order_id', orderId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

/**
 * 取得當前登入的管理員資訊
 */
export async function getCurrentAdmin(): Promise<AdminInfo | null> {
  try {
    console.log('🔍 getCurrentAdmin - 開始檢查管理員身份');

    // 從 Supabase Auth 取得當前登入的使用者
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) {
      console.error('❌ getCurrentAdmin - 取得使用者失敗:', userError);
      return null;
    }

    if (!user) {
      console.warn('⚠️ getCurrentAdmin - 沒有登入的使用者');
      return null;
    }

    console.log('✅ getCurrentAdmin - Supabase Auth User ID:', user.id);
    console.log('📧 getCurrentAdmin - User Email:', user.email);

    // 先用 auth_user_id 查詢
    let { data: admin, error: adminError } = await supabase
      .from('admin_profiles')
      .select('id, name, email, role, auth_user_id, status, is_active')
      .eq('auth_user_id', user.id)
      .eq('status', 'active')
      .eq('is_active', true)
      .maybeSingle();

    if (adminError) {
      console.error('[getCurrentAdmin] admin_profiles query error:', adminError);
      return null;
    }

    // ── Phase 1 原則：auth_user_id 是唯一身份來源 ──────────────
    // Email fallback 已廢棄（Phase 1 Task B），不再以 email 作為識別依據。
    // 若用 auth_user_id 查不到，代表該使用者確實沒有 admin 身份。
    if (!admin) {
      console.warn('[getCurrentAdmin] no admin_profiles found for auth_user_id:', user.id, '(email fallback removed per Phase 1)');
      return null;
    }

    console.log('[getCurrentAdmin] admin_verified – id:', admin.id, '/ role:', admin.role);

    return {
      id: admin.id,
      name: admin.name,
      email: admin.email,
      role: admin.role
    };

  } catch (error) {
    console.error('❌ getCurrentAdmin - 發生錯誤:', error);
    return null;
  }
}
